// @ts-check
import 'whatwg-fetch'
import { Broker } from './utils/broker'
import { createCmp } from './cmp/Cmp'
import Ui from './ui/Ui'
import { createLog } from './utils/logger'
import clientApi from './clientApi'
import loadStorageIframe from './storage-iframe'
import Persist from './persist/Persist'
import * as Config from './config/config'
import { fetchVendorLists } from './utils/fetch-vendor-lists'

// https://vendorlist.consensu.org/v3/vendor-list.json

const log = createLog()

/**
 * @param {Broker} broker
 * @param {Config.Config<ClientConfig>} config
 */
function createUi(broker, config) {
  const {
    uiLevel,
    renderPlatform,
    brand,
    uiScriptUrl,
    consentOrPay,
    isMailEssentialSubscriber,
    consentOrPaySubscriptionURL,
    isUserLoggedIn,
    allowRejectAll,
    tcfCacheKey
  } = config.get()
  return new Ui({
    uiLevel,
    renderPlatform,
    brand,
    uiScriptUrl,
    clientBroker: broker,
    consentOrPay,
    isMailEssentialSubscriber,
    consentOrPaySubscriptionURL,
    isUserLoggedIn,
    allowRejectAll,
    tcfCacheKey
  })
}

/**
 * @param {Broker} broker
 * @param {Config.Config<ClientConfig>} clientConfig
 */
async function launch(broker, clientConfig) {
  const {
    brokerId,
    controllerScriptBase,
    backendUrl,
    channel,
    platform,
    renderPlatform,
    storeUserPreference,
    uuid,
    showUi,
    uiLevel,
    cmpModel,
    consentOrPay,
    dissentUser,
    domain,
    persistTags
  } = clientConfig.get()
  const { tcData, iframe } =
    !!storeUserPreference &&
    (await loadStorageIframe(broker, brokerId, controllerScriptBase))
  const cmpConfig = Config.createCmpConfig(clientConfig, tcData)
  const {
    gvlVersion,
    cvlVersion,
    nrvVersion,
    migratedGvlVersion,
    ...remainingCmpConfig
  } = cmpConfig.get()
  const { gvl, cvl, nrv, newGvl } = await fetchVendorLists({
    gvlVersion,
    cvlVersion,
    nrvVersion,
    migratedGvlVersion
  })

  if (typeof gvl === 'string') {
    throw new Error(`unable to download GVL version: ${gvlVersion}`)
  }

  if (typeof cvl === 'string') {
    throw new Error(`unable to download CVL version: ${cvlVersion}`)
  }

  if (typeof nrv === 'string') {
    throw new Error(`unable to download NVL version: ${nrvVersion}`)
  }

  const newGvlRaw = newGvl && typeof newGvl !== 'string' ? newGvl : undefined

  log('gvlJson', gvl.version, gvl.json)
  log('cvlJson', cvl.json)
  log('nrvJson', nrv.json)
  log('newGvlJson', newGvlRaw?.json)

  const persist = new Persist({
    backendUrl,
    channel,
    platform: platform || renderPlatform,
    cmpModel,
    domain,
    tags: persistTags
  })
  const ui = createUi(broker, clientConfig)
  /**
   * @type import('./cmp/Cmp').CreateCmpOpts
   */
  const cmpOpts = {
    cvlJson: cvl.json,
    cvlVersion,
    gvlRaw: gvl,
    gvlVersion: gvl.version,
    newGvlRaw,
    nrvJson: nrv.json,
    nrvVersion,
    persist,
    storeTcString: !!storeUserPreference,
    ui,
    uuid,
    ...remainingCmpConfig
  }
  const cmp = await createCmp(cmpOpts)
  const _showUi = showUi === 'true' || showUi === true
  broker.emit('is_loaded')
  const message = {
    ns: '@mol-ads/mol-ads-cmp',
    id: brokerId,
    sender: 'mol-ads/mol-ads-cmp',
    action: 'is_loaded'
  }
  if (iframe) {
    iframe.contentWindow.postMessage(message, '*')
  }

  await cmp.isReady()
  clientApi(broker, cmp, ui)
  if (_showUi) {
    cmp.openUi(uiLevel)
  } else if (consentOrPay && dissentUser) {
    log('dissenting user')
    cmp.setCurrentLevel('Level 1')
    cmp.dissentToAll()
    cmp.notifyUsersPreferences()
  } else {
    cmp.notifyUsersPreferences()
  }
  broker.emit('is_ready')
}

setTimeout(() => {
  const scriptTag = document.getElementById('mol-ads-tcfv2-script')
  /**
   * @type {Config.Config<ClientConfig>}
   **/
  const config = Config.createClientConfig(
    JSON.parse(scriptTag?.dataset.opts || '{}')
  )
  const { brokerId } = config.get()
  const broker = new Broker('@mol-ads/mol-ads-cmp', 'mol-ads-cmp', brokerId)
  launch(broker, config)
}, 10)

/**
 * @typedef {import('./config/config').ClientConfig} ClientConfig
 */
